import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, Dumbbell, ClipboardList, FileText, LogOut, CheckCircle2, CalendarCheck, ArrowRight, Video, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ExerciseCard from "@/components/patient/ExerciseCard";
import QuestionnaireForm from "@/components/patient/QuestionnaireForm";
import { useAuth, formatApiError } from "@/auth/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ_DAYS = {
  1: [1],
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 3, 4, 5],
  6: [1, 2, 3, 4, 5, 6],
  7: [0, 1, 2, 3, 4, 5, 6],
};

const isDueToday = (freq) => {
  const f = Math.min(Math.max(parseInt(freq, 10) || 1, 1), 7);
  return (FREQ_DAYS[f] || []).includes(new Date().getDay());
};

export default function PatientAreaPage() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [letters, setLetters] = useState([]);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  const fetchProgram = useCallback(async () => {
    try {
      const [programRes, lettersRes] = await Promise.all([
        axios.get(`${API}/my/program`, { withCredentials: true }),
        axios.get(`${API}/my/letters`, { withCredentials: true }),
      ]);
      setData(programRes.data);
      setLetters(lettersRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, []);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user) fetchProgram();
  }, [user, navigate, fetchProgram]);

  const handleToggle = async (exerciseId, completed) => {
    try {
      await axios.post(
        `${API}/my/program/complete`,
        { exercise_id: exerciseId, completed },
        { withCredentials: true }
      );
      setData((prev) => ({
        ...prev,
        program: {
          ...prev.program,
          completions: { ...(prev.program?.completions || {}), [exerciseId]: completed },
        },
      }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (user === null || (user && !data)) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="pt-48 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
        </div>
      </div>
    );
  }
  if (!user) return null;

  const program = data?.program;
  const completions = program?.completions || {};
  const items = program?.items?.filter((i) => i.exercise) || [];
  const completedCount = items.filter((i) => completions[i.exercise_id]).length;
  const todayItems = items.filter((i) => isDueToday(i.weekly_frequency));

  return (
    <div className="min-h-screen bg-stone-50" data-testid="patient-area-page">
      <Header />
      <main className="pt-40 sm:pt-60 pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
                {t("patientArea.label")}
              </span>
              <h1 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-2">
                {t("patientArea.title")}
              </h1>
              <p className="text-stone-600 text-sm mt-2" data-testid="patient-welcome">
                {t("patientArea.welcome")}, {user.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/le-mie-lettere">
                <Button variant="outline" size="sm" className="rounded-full border-stone-300 text-stone-600">
                  <FileText className="w-4 h-4 mr-1.5" />
                  {t("patientArea.myLetters")}
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="rounded-full border-stone-300 text-stone-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1.5" />
                {t("auth.logout")}
              </Button>
            </div>
          </div>

          {/* No premium access */}
          {!data.has_access && (
            <div className="bg-white rounded-3xl p-8 sm:p-12 text-center border border-stone-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)]" data-testid="no-access-card">
              <div className="w-16 h-16 rounded-full bg-[#e8ece6] flex items-center justify-center mx-auto mb-6">
                <Lock className="w-7 h-7 text-[#2c4c3b]" />
              </div>
              <h2 className="font-serif text-2xl text-stone-900 mb-3">{t("patientArea.noAccess")}</h2>
              <p className="text-stone-600 text-sm max-w-md mx-auto mb-8">{t("patientArea.noAccessText")}</p>
              <Link to="/pricing">
                <Button className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-8 py-6">
                  {t("patientArea.discoverPlans")}
                </Button>
              </Link>
            </div>
          )}

          {data.has_access && (
            <>
              {/* Monthly 1:1 consultation reminder (luxury only) */}
              {data.plan_id === "luxury" && (
                <div className="bg-[#f0e9dc] border border-[#d4c4a8] rounded-3xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="consult-reminder">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-[#2c4c3b]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-stone-900">{t("patientArea.consultTitle")}</h3>
                    {data.next_appointment ? (
                      <p className="text-sm text-stone-600 mt-1" data-testid="consult-next">
                        {t("patientArea.consultBooked")}{" "}
                        <strong className="text-[#2c4c3b]">
                          {data.next_appointment.date} — {data.next_appointment.time_slot}
                        </strong>
                      </p>
                    ) : (
                      <p className="text-sm text-stone-600 mt-1">{t("patientArea.consultText")}</p>
                    )}
                  </div>
                  {!data.next_appointment && (
                    <Link to="/booking">
                      <Button className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full" data-testid="consult-book-btn">
                        {t("patientArea.consultCta")}
                      </Button>
                    </Link>
                  )}
                </div>
              )}

              {/* Questionnaire */}
              {!data.questionnaire && !showQuestionnaire && (
                <div className="bg-[#e8ece6] rounded-3xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="questionnaire-banner">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="w-5 h-5 text-[#2c4c3b]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-stone-900">{t("patientArea.questionnaireBanner")}</h3>
                    <p className="text-sm text-stone-600 mt-1">{t("patientArea.questionnaireBannerText")}</p>
                  </div>
                  <Button
                    onClick={() => setShowQuestionnaire(true)}
                    className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full"
                    data-testid="open-questionnaire-btn"
                  >
                    {t("patientArea.fillQuestionnaire")}
                  </Button>
                </div>
              )}
              {showQuestionnaire && !data.questionnaire && (
                <div className="mb-8">
                  <QuestionnaireForm
                    t={t}
                    onDone={(q) => {
                      setData((prev) => ({ ...prev, questionnaire: q }));
                      setShowQuestionnaire(false);
                    }}
                  />
                </div>
              )}
              {data.questionnaire && (
                <div className="flex items-center gap-2 text-sm text-[#2c4c3b] mb-8" data-testid="questionnaire-done">
                  <CheckCircle2 className="w-4 h-4" />
                  {t("patientArea.questionnaireDone")}
                </div>
              )}

              {/* Program */}
              {!program || items.length === 0 ? (
                <div className="bg-white rounded-3xl p-8 sm:p-12 text-center border border-stone-100" data-testid="no-program-card">
                  <div className="w-16 h-16 rounded-full bg-[#e8ece6] flex items-center justify-center mx-auto mb-6">
                    <Dumbbell className="w-7 h-7 text-[#2c4c3b]" />
                  </div>
                  <h2 className="font-serif text-2xl text-stone-900 mb-3">{t("patientArea.noProgram")}</h2>
                  <p className="text-stone-600 text-sm max-w-md mx-auto">{t("patientArea.noProgramText")}</p>
                </div>
              ) : (
                <>
                  {/* Today's plan */}
                  <div className="bg-[#2c4c3b] rounded-3xl p-6 mb-6" data-testid="today-card">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#8a9a86]/30 flex items-center justify-center">
                        <CalendarCheck className="w-5 h-5 text-[#d4c4a8]" />
                      </div>
                      <h3 className="font-serif text-xl text-stone-100">{t("patientArea.todayTitle")}</h3>
                    </div>
                    {todayItems.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {todayItems.map((i) => (
                          <span
                            key={i.exercise_id}
                            className={`text-xs px-3 py-1.5 rounded-full ${
                              completions[i.exercise_id]
                                ? "bg-[#8a9a86]/40 text-stone-300 line-through"
                                : "bg-stone-100 text-[#2c4c3b] font-medium"
                            }`}
                          >
                            {i.exercise.title}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-stone-300 mt-1">{t("patientArea.todayNone")}</p>
                    )}
                  </div>

                  {/* Progress */}
                  <div className="bg-white rounded-3xl p-6 border border-stone-100 mb-6" data-testid="progress-card">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-stone-700">{t("patientArea.progress")}</span>
                      <span className="text-sm font-semibold text-[#2c4c3b]">
                        {completedCount}/{items.length}
                      </span>
                    </div>
                    <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#2c4c3b] rounded-full transition-all duration-500"
                        style={{ width: `${items.length ? (completedCount / items.length) * 100 : 0}%` }}
                      />
                    </div>
                    {data.streak_weeks > 0 && (
                      <div className="flex items-center gap-2 mt-4 bg-[#f0e9dc] rounded-full px-4 py-2 w-fit" data-testid="streak-badge">
                        <Flame className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-medium text-[#2c4c3b]">
                          {data.streak_weeks}{" "}
                          {t(data.streak_weeks === 1 ? "patientArea.streakWeek" : "patientArea.streakWeeks")}
                        </span>
                      </div>
                    )}
                    {program.notes && (
                      <p className="text-sm text-stone-600 mt-4 italic border-t border-stone-100 pt-4">
                        {program.notes}
                      </p>
                    )}
                  </div>

                  <div className="space-y-5" data-testid="exercises-list">
                    {items.map((item) => (
                      <ExerciseCard
                        key={item.exercise_id}
                        item={item}
                        completed={!!completions[item.exercise_id]}
                        dueToday={isDueToday(item.weekly_frequency)}
                        onToggle={handleToggle}
                        t={t}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* My Letters */}
          {letters.length > 0 && (
            <div className="mt-12" data-testid="my-letters-section">
              <h2 className="font-serif text-2xl text-stone-900 mb-5">{t("patientArea.myLetters")}</h2>
              <div className="space-y-3">
                {letters.map((letter) => (
                  <Link
                    key={letter.id}
                    to={`/lettera/${letter.id}`}
                    onClick={() => sessionStorage.setItem("patientEmail", user.email)}
                    className="block bg-white rounded-2xl p-5 border border-stone-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all group"
                    data-testid={`my-letter-${letter.id}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#e8ece6] flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-[#2c4c3b]" />
                        </div>
                        <div>
                          <h3 className="font-serif text-lg text-stone-900 group-hover:text-[#2c4c3b] transition-colors">
                            {letter.title}
                          </h3>
                          <p className="text-sm text-[#8a9a86]">{letter.month}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-stone-300 group-hover:text-[#2c4c3b] group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
